/**
 * @file src/input/CosmicLFO.js
 * @description Manages the Cosmic LFO subsystem that drives orbiters via procedural modulation,
 * including UI state and parameter bindings.
 */

import notifications from '../core/AppNotifications.js';
import { PARAM_INTERNAL_PRECISION_DECIMALS } from '../core/ParameterManager.js';
import { getScopedState, setScopedState } from '../core/stackUtils.js';
// The pure oscillator + frequency kernels live in standalone TS modules.
// This facade owns only the impure parts (scoped state, PM push, room bridge)
// and delegates all maths here.
import { sample as oscSample } from './cosmic/LFOOscillator.ts';
import {
  MIN_FREQUENCY_HZ,
  toFiniteNumber,
  sanitizeFrequency,
  ensureHarmonicRange,
  applyMultiplier,
} from './cosmic/FrequencySourceManager.ts';
// The source / waveform MODEL (identity, labels, icons) is the single source of truth in
// the catalog — both this facade and the React panel read it so the list can't drift
// ([[no-two-sources-of-truth]]).
import {
  MANUAL_SOURCE_KEY,
  DEFAULT_COSMIC_SOURCE_KEY,
  SUBDIVISION_SOURCE_KEY,
  isSubdivisionSource,
  COSMIC_SOURCES,
  COSMIC_DISCRETE_SOURCES,
  COSMIC_WAVEFORM_KEYS,
} from './cosmic/cosmicSources.ts';
// The musical RATE vocabulary (note values + bar multiples) the tempo-locked source runs on.
import { DEFAULT_NOTE_RATE_KEY, isNoteRateKey, noteRateToHz } from '../sync/noteRates.js';
// The deck owns the tempo this locks to — the same one the launch grid and the loop presets
// project against, and the shared BPM when the deck is synced.
import { deckFor } from '../voice/Deck.js';
// Route the PM push + scope resolution through the shared input seam.
import { getPriority } from '../config/Constants.js';
import { createInputRouter } from './source/InputRouter.ts';
import { resolveScopedContext } from './source/ScopingResolver.ts';
// Cosmic surface: the dash-form PM param id (`<axis>-cosmic-frequency`) the React
// manual-freq knob + monitor bind to. CosmicLFO bridges to it so the React knob drives the
// LFO and LFO-internal changes (kick / source switch) reflect on the knob + monitor.
import { cosmicFrequencyParamId } from './cosmicFrequencyParam.js';

const ALLOWED_WAVEFORMS = COSMIC_WAVEFORM_KEYS;

const SOURCE_DEFINITIONS = Object.fromEntries(
  COSMIC_DISCRETE_SOURCES.map((s) => [s.key, { label: s.label }]),
);

/**
 * Derive the frequency slot map — every discrete catalog source the world payload carries a
 * numeric value for, keyed by itself, plus the manual entry. The stored-state fallback scan
 * reads it to promote a real source when a dimension has no stored pick.
 * @param {Record<string, unknown>} sources - Extracted `{ sourceKey: frequencyHz }` payload.
 * @returns {Record<string, string>}
 */
export function deriveFrequencySlotMap(sources = {}) {
  const slotMap = { [MANUAL_SOURCE_KEY]: MANUAL_SOURCE_KEY };
  Object.keys(SOURCE_DEFINITIONS).forEach((key) => {
    if (typeof sources[key] === 'number') {
      slotMap[key] = key;
    }
  });
  return slotMap;
}

function shouldDebugCosmicLfo() {
  try {
    if (typeof window !== 'undefined') {
      return Boolean(window.__DEBUG_COSMIC_LFO);
    }
  } catch (_) {}
  return false;
}

function debugLog(instance, message, detail = {}) {
  const enabled = instance?.debug || shouldDebugCosmicLfo();
  if (!enabled) return;
  try {
    // gated by window.__DEBUG_COSMIC_LFO or instance.debug
    console.debug(`[CosmicLFO:${instance?.axis}] ${message}`, detail);
  } catch (_) {
    // ignore logging errors
  }
}

/**
 * @class CosmicLFO
 * @description A low-frequency oscillator (LFO) that derives its frequency settings from exoplanet data.
 * Allows waveforms like sine, saw, triangle, square, random, etc.
 */
export class CosmicLFO {

  /**
   * Constructor for a new CosmicLFO instance.
   * @param {string} axis - The axis this LFO controls ('x', 'y', or 'z').
   * @param {ParameterManager} parameterManager - The owning voice's ParameterManager (DI).
   * @param {{ eventBus?: EventTarget, voiceId?: string|null }} [options] `voiceId` names the deck
   *   this LFO locks to when its source is tempo-locked. Omitted (single-orbiter) it falls back to
   *   the active voice, which is the same deck; a multi tile MUST pass its own or every tile would
   *   read the focused deck's tempo instead of its own.
   */
  constructor(axis, parameterManager, { eventBus, voiceId = null } = {}) {
    this.axis = axis;
    this.parameterManager = parameterManager;
    this._voiceId = voiceId;
    // The per-voice event bus this LFO mirrors its non-PM cosmic state changes onto
    // (the React `cosmic` surface subscribes to the SAME bus). Defaults to `window` so single-orbiter is
    // byte-identical; a multi tile injects its own EventTarget so one voice's cosmic toggle doesn't
    // re-read another voice's React surface.
    this._eventBus = eventBus ?? (typeof window !== 'undefined' ? window : null);
    // The seam this LFO pushes through. Lazily bound (see _inputSource()).
    this._inputSourceHandle = null;

    // Multidimensional LFO management (per dimension oscillator state)
    // Maps dimensionId -> { isActive, phase, frequency, amplitude, waveform }
    this.dimensionLFOs = new Map();
    
    // UI and state (shared across all dimensions)
    this.isActive = false; // True when any dimension is actively running
    this.waveform = 'sine';
    this.baseFrequency = 0.01;
    this.amplitude = 1.0;
    this.visualSampleRate = 30;
    this._samplerHandle = null;
    this._samplerMode = null;
    this._visualSamplerActive = false;
    this._activeDimensions = new Set();
    this.debug = false;
    // Before world data arrives there is no astronomical value to modulate from, so the honest
    // starting point is the manual knob — naming a world source here would put the Cosmic LFO
    // label on a frequency no world supplied. World data seeding picks the real source.
    this.currentExoplanet = MANUAL_SOURCE_KEY;
    this.currentMultiplier = 1;
    this.currentFrequencySource = MANUAL_SOURCE_KEY;
    this.sourceBaseFrequency = this.baseFrequency;
    this.frequencySources = {};
    this.frequencySlotMap = { manual: MANUAL_SOURCE_KEY };
    this._lastVisualTimestamp = null;
    this._boundDimensionListener = null;
    this._duringScopedApply = false;
    this._pendingSourceState = null;
    this._currentWaveformSelection = `${this.axis}-waveform-${this.waveform}`;
    this._lastPushedParamValue = null;
    this._sourcesInitialized = false;
    this._boundVisibilityChange = null;
    // Cosmic surface: the CosmicLFO ↔ `<axis>-cosmic-frequency` PM bridge (the React
    // manual-freq knob + monitor bind to that param). `_freqParamSource` is the write-back
    // tag so our own publish is ignored by our subscription (the param is bidirectional, so
    // PM re-notifies even the writer). `_applyingFreqFromParam` guards the inbound apply from
    // re-publishing. See cosmicFrequencyBridge.test.js for the pinned PM contract.
    this._freqParamBound = false;
    this._freqParamController = null;
    this._applyingFreqFromParam = false;
    this._freqParamSource = `CosmicLFO:freq:${this.axis}`;
    // Same bridge for the `<axis>-cosmic-amplitude` PM param (the React amplitude knob).
    this._ampParamBound = false;
    this._ampParamController = null;
    this._applyingAmpFromParam = false;
    this._ampParamSource = `CosmicLFO:amp:${this.axis}`;
    // The room bridge (see setRoomBridge). Null outside a room, which keeps every path below
    // exactly as it was.
    this._roomBridge = null;
    // TEMPO-LOCKED source: which musical rate the LFO runs at, and the deck subscription that
    // re-derives its frequency when the tempo (or the meter, for bar-length rates) moves. The
    // subscription exists ONLY while this source is selected — see `_bindTempoFollow`.
    this.currentNoteRate = DEFAULT_NOTE_RATE_KEY;
    this._deckUnsub = null;

    if (typeof document !== 'undefined') {
      this._boundVisibilityChange = () => this._onVisibilityChange();
      document.addEventListener('visibilitychange', this._boundVisibilityChange, { passive: true });
    }

    // Re-apply this axis' stored per-dimension scoped state whenever the active dimension changes.
    // (Previously registered inside the legacy `attachSwitch`; hoisted here so it survives the WAC
    // switch removal — `_applyScopedStateFromRegistry` is the only driver of scoped state on dim change.)
    if (typeof document !== 'undefined') {
      this._boundDimensionListener = () => this._applyScopedStateFromRegistry();
      document.addEventListener('orbiters:dimension-changed', this._boundDimensionListener);
    }
  }
  getScopedContext() {
    // Scope resolution consolidated into the shared resolver (was duplicated across
    // CosmicLFO / Sensors / MIDI). CosmicLFO keeps its extra ParameterManager fallback (used
    // when the world-mode controller has no active dimension) by passing parameterManager+axis.
    return resolveScopedContext({ parameterManager: this.parameterManager, axis: this.axis });
  }

  /**
   * Lazily bind the {@link InputSource} this LFO pushes through, created from the
   * ParameterManager. Priority comes from the single source of truth (PRIORITY_MAP), per axis.
   */
  _inputSource() {
    if (!this.parameterManager) return null;
    if (!this._inputSourceHandle) {
      // A GENERATOR: this handle carries the oscillator's axis stream — absolute samples where
      // every tick supersedes the last, nobody's hand. The knob write-back tags
      // (`_freqParamSource` / `_ampParamSource`) are deliberately NOT minted here: a knob commit
      // is a hand's edit and must never be thinned or bypass the room's edit bookkeeping.
      this._inputSourceHandle = createInputRouter(this.parameterManager)
        .source(`CosmicLFO:${this.axis}`, getPriority(`cosmic-${this.axis}`), { stream: 'generator' });
    }
    return this._inputSourceHandle;
  }

  getComponentId() {
    return `${this.axis}.cosmic-toggle`;
  }

  /**
   * Install (or clear, with null) the ROOM bridge — the seam that makes this LFO's discrete
   * controls shared instead of private. When present:
   *
   * - a user act (enable, source, waveform, a ×0.5/×2 kick) is applied locally AND published, so
   *   pressing it once changes it for everybody; `canControl` decides whose input may drive this
   *   deck at all. No lease is taken and none is asked for — a lease is a hand on a knob, and
   *   nobody's hand rests on a button, so either player may press it again at any time.
   * - `canGenerate` names the ONE client whose LFO actually writes the axis. Every client runs its
   *   own instance and they now share every setting, but not a phase: if they all pushed values the
   *   room would hear as many different sweeps as there are players. So the others keep the state
   *   and the panel, and take the value stream off the wire like any other routed parameter.
   *
   * A remote act arrives back through the same public methods with `{ roomRemote: true }`, which
   * skips both the gate and the re-publish. Null (solo, or the room ending) keeps every path
   * byte-identical to no-room.
   *
   * @param {{ canControl?: () => boolean, canGenerate?: () => boolean,
   *           publishEnable?: (on: boolean) => void, publishSource?: (key: string) => void,
   *           publishWaveform?: (key: string) => void, publishKick?: (step: string) => void,
   *           publishNoteRate?: (key: string) => void }|null} bridge
   */
  setRoomBridge(bridge) {
    this._roomBridge = bridge && typeof bridge === 'object' ? bridge : null;
  }

  /** Does the room refuse this USER act? A remote apply and an internal per-dimension restore are
   *  neither user acts nor negotiable — they pass. */
  _roomRefuses(roomRemote) {
    if (roomRemote || this._duringScopedApply || !this._roomBridge) return false;
    return this._roomBridge.canControl?.() === false;
  }

  /** Share an applied user act with the room. Never an echo of a remote one, never a restore. */
  _shareRoomAct(roomRemote, method, ...args) {
    if (roomRemote || this._duringScopedApply || !this._roomBridge) return;
    try {
      this._roomBridge[method]?.(...args);
    } catch (error) {
      console.warn(`[CosmicLFO:${this.axis}] Failed to publish ${method}`, error);
    }
  }

  /** Is this client the one whose LFO drives the axis? True whenever no room says otherwise. */
  _roomGenerates() {
    return !this._roomBridge || this._roomBridge.canGenerate?.() !== false;
  }

  // ── tempo-locked source ───────────────────────────────────────────────────

  /** The deck this LFO locks to. Null before the voice has one (pre-boot, tests). */
  _deck() {
    try {
      return deckFor(this._voiceId);
    } catch (_error) {
      return null;
    }
  }

  /** Whether the ACTIVE dimension's source is the tempo-locked one. */
  _isTempoLocked() {
    return isSubdivisionSource(this.currentFrequencySource);
  }

  /** The musical rate this LFO runs at when tempo-locked (e.g. '1/4', '1bar'). */
  getNoteRate() {
    return this.currentNoteRate || DEFAULT_NOTE_RATE_KEY;
  }

  /**
   * Pick the musical RATE. A discrete act like the source and waveform selects, so it is gated and
   * published the same way, and it only means anything while the tempo-locked source is selected.
   * @param {string} key - A rate key from the vocabulary (`noteRates.js`).
   * @param {{ roomRemote?: boolean }} [options] `roomRemote` marks a peer's pick being applied.
   */
  setNoteRate(key, { roomRemote = false } = {}) {
    if (!isNoteRateKey(key)) return;
    if (this._roomRefuses(roomRemote)) {
      this._notifyCosmicChange();
      return;
    }
    this.currentNoteRate = key;
    const { dimensionId } = this.getScopedContext();
    const dimensionLFO = dimensionId ? this._ensureDimensionState(dimensionId) : null;
    if (dimensionLFO) dimensionLFO.currentNoteRate = key;
    // Only the tempo-locked source runs on a rate; picking one while another source is selected
    // remembers it for when the user switches, and changes nothing audible now.
    if (this._isTempoLocked()) this._applyNoteRateFrequency();
    if (!this._duringScopedApply) {
      this._persistCosmicState({ sourceState: this._createSourceState() });
    }
    this._notifyCosmicChange();
    this._shareRoomAct(roomRemote, 'publishNoteRate', key);
  }

  /**
   * Re-derive the frequency from the deck's tempo and the picked rate, and drive the LFO with it.
   *
   * The fold is done HERE, explicitly, and not left to `setBaseFrequency` — that CLAMPS, and a
   * clamped rate is a lie: at 174 BPM a 1/32 asks for 23.2 Hz, and pinning it to the 21 Hz ceiling
   * would leave the picker saying "1/32" over a rate that is no division of the bar at all. Halving
   * into range keeps it musical — the same note an octave slower, which at that tempo is the 1/16
   * sitting right next to it in the list.
   */
  _applyNoteRateFrequency() {
    const deck = this._deck();
    const bpm = Number(deck?.tempo);
    const raw = noteRateToHz(this.getNoteRate(), bpm, deck?.meter);
    if (!Number.isFinite(raw) || raw <= 0) return; // no tempo yet — the bind re-derives on 'bpm'
    const hz = ensureHarmonicRange(raw);
    if (!Number.isFinite(hz) || hz <= 0) return;
    // Same options a multiplier kick uses: the rate IS the source, so assigning one must not
    // reset it, and the derived value is not a new source base to compute future rates from.
    this.setBaseFrequency(hz, {
      skipSourceAssignment: true,
      skipMultiplierReset: true,
      skipSourceBaseUpdate: true,
    });
  }

  /**
   * Match the deck subscription to the selected source — bound while tempo-locked, dropped
   * otherwise. Idempotent, and called from every place the source can change rather than pairing a
   * bind with an unbind at each one: the source is reassigned from several directions that have
   * nothing to do with the picker (a dimension restore, world data arriving and seeding its own
   * default, a frequency write that implies manual), and any of those left holding a subscription
   * would keep this LFO listening to a deck it no longer follows.
   */
  _syncTempoFollow() {
    if (this._isTempoLocked()) this._bindTempoFollow();
    else this._unbindTempoFollow();
  }

  /**
   * Follow the deck while tempo-locked. ONE subscription — the frequency is re-derived on a tempo or
   * meter change and at no other time, so a session sitting at one tempo costs nothing at all. Meter
   * counts because a bar-length rate is a different number of beats in 6/8 than in 4/4.
   */
  _bindTempoFollow() {
    if (this._deckUnsub) return;
    const deck = this._deck();
    if (!deck?.onChange) return;
    this._deckUnsub = deck.onChange((_snapshot, reason) => {
      if (reason !== 'bpm' && reason !== 'meter') return;
      if (!this._isTempoLocked()) return;
      this._applyNoteRateFrequency();
    });
  }

  /** Drop the deck subscription (source switched away, or teardown). */
  _unbindTempoFollow() {
    if (!this._deckUnsub) return;
    try {
      this._deckUnsub();
    } catch (_error) {
      // deck already gone
    }
    this._deckUnsub = null;
  }

  _persistToggleState(isActive) {
    const { stackId, dimensionId } = this.getScopedContext();
    if (!stackId || !dimensionId) return;
    try {
      setScopedState(stackId, this.getComponentId(), Boolean(isActive), { dimensionId });
    } catch (error) {
      console.warn('[CosmicLFO] Failed to persist toggle state:', error);
    }
    this._persistCosmicState();
  }

  getStoredToggleState() {
    const { stackId, dimensionId } = this.getScopedContext();
    if (!stackId || !dimensionId) return undefined;
    try {
      return getScopedState(stackId, this.getComponentId(), { dimensionId });
    } catch (error) {
      console.warn('[CosmicLFO] Failed to read toggle state:', error);
      return undefined;
    }
  }

  _getComponentIds() {
    const axis = this.axis;
    return {
      toggle: this.getComponentId(),
      waveform: `${axis}.waveform`,
      amplitude: `${axis}.cosmic-amplitude`,
      frequency: `${axis}.cosmic-frequency`,
      source: `${axis}.exo-source`,
    };
  }

  _createSourceState() {
    const { dimensionId } = this.getScopedContext();
    const dimensionLFO = dimensionId ? this.dimensionLFOs.get(dimensionId) : null;

    // Prefer dimension-specific state when available
    if (dimensionLFO) {
      return {
        sourceKey: dimensionLFO.currentFrequencySource ?? this.currentFrequencySource,
        slotId: dimensionLFO.currentExoplanet ?? this.currentExoplanet,
        multiplier: dimensionLFO.currentMultiplier ?? this.currentMultiplier,
        baseFrequency: dimensionLFO.sourceBaseFrequency ?? this.sourceBaseFrequency,
        // Carried whatever the source is: a dimension switched away from a tempo-locked LFO and
        // back must return to the rate it was on, not to the default.
        noteRate: dimensionLFO.currentNoteRate ?? this.getNoteRate(),
      };
    }

    // Fallback to shared state
    return {
      sourceKey: this.currentFrequencySource,
      slotId: this.currentExoplanet,
      multiplier: this.currentMultiplier,
      baseFrequency: this.sourceBaseFrequency,
      noteRate: this.getNoteRate(),
    };
  }

  _persistCosmicState(overrides = {}) {
    if (this._duringScopedApply) return;
    const { stackId, dimensionId } = this.getScopedContext();
    if (!stackId || !dimensionId) return;
    const ids = this._getComponentIds();
    try {
    const waveformSelection =
      overrides.waveform ??
      this._currentWaveformSelection ??
      `${this.axis}-waveform-${this.waveform}`;
      setScopedState(stackId, ids.waveform, waveformSelection, { dimensionId });

      const amplitudeValue = overrides.amplitude ?? this.amplitude;
      setScopedState(stackId, ids.amplitude, amplitudeValue, { dimensionId });

      const frequencyValue = overrides.baseFrequency ?? this.baseFrequency;
      setScopedState(stackId, ids.frequency, frequencyValue, { dimensionId });

      const sourceState = overrides.sourceState ?? this._createSourceState();
      setScopedState(stackId, ids.source, sourceState, { dimensionId });
    } catch (error) {
      console.warn('[CosmicLFO] Failed to persist cosmic state:', error);
    }
  }

  _applyStoredSourceState(sourceState, storedFrequency) {
    if (typeof sourceState === 'string') {
      sourceState = {
        sourceKey: sourceState,
        slotId: sourceState,
      };
    }
    const hasSources = Object.keys(this.frequencySources || {}).length > 0;
    debugLog(this, '_applyStoredSourceState:input', {
      sourceState,
      storedFrequency,
      hasSources,
    });
    if (!sourceState) {
      if (Number.isFinite(storedFrequency)) {
        if (hasSources || this._sourcesInitialized) {
          this.setFrequencySource(MANUAL_SOURCE_KEY);
          this.setBaseFrequency(storedFrequency, {
            skipSourceAssignment: true,
            skipMultiplierReset: false,
            skipSourceBaseUpdate: false,
          });
          return true;
        }
        // Defer applying default frequency until sources are available
        this._pendingSourceState = { source: null, frequency: storedFrequency };
        return false;
      }
      return true;
    }

  const {
      sourceKey: rawSourceKey = MANUAL_SOURCE_KEY,
      slotId: rawSlotId = MANUAL_SOURCE_KEY,
      multiplier = 1,
      baseFrequency = this.sourceBaseFrequency,
      noteRate: storedNoteRate = null,
    } = sourceState || {};

    // The rate this dimension was left on, restored whatever its source is — switching away from a
    // tempo-locked LFO and back must return to the rate the user picked, not to the default.
    //
    // Written to the DIMENSION record as well as the shared field, because the two are separate
    // copies of the same fact and the source switch below reads the dimension's. Restoring only the
    // shared one let the stale dimension copy overwrite it, and the rate silently reverted to
    // whatever was last picked anywhere.
    if (isNoteRateKey(storedNoteRate)) {
      this.currentNoteRate = storedNoteRate;
      const { dimensionId: restoreDim } = this.getScopedContext();
      const restoreState = restoreDim ? this._ensureDimensionState(restoreDim) : null;
      if (restoreState) restoreState.currentNoteRate = storedNoteRate;
    }

    // TEMPO-LOCKED restores BEFORE anything below reads `hasSources`: its frequency comes from the
    // deck, not from world data, so a world carrying no frequency sources must not defer or divert
    // it the way it would a discrete source.
    if (rawSourceKey === SUBDIVISION_SOURCE_KEY) {
      this._setFrequencySourceImpl(SUBDIVISION_SOURCE_KEY);
      return true;
    }

    let effectiveSourceKey = rawSourceKey;
    let effectiveSlotId = rawSlotId;

    const frequencyCandidate = Number.isFinite(storedFrequency)
      ? storedFrequency
      : Number.isFinite(baseFrequency)
        ? baseFrequency
        : null;

    if (
      effectiveSourceKey === MANUAL_SOURCE_KEY &&
      hasSources &&
      (frequencyCandidate == null || frequencyCandidate <= MIN_FREQUENCY_HZ * 1.001)
    ) {
      effectiveSourceKey = null;
      effectiveSlotId = null;
    }

    if (effectiveSourceKey !== MANUAL_SOURCE_KEY && effectiveSourceKey !== null && !hasSources) {
      if (!this._sourcesInitialized) {
        this._pendingSourceState = { source: sourceState, frequency: storedFrequency };
        return false;
      }
      // Sources ARE initialised and this world still supplies none, so a stored discrete pick has
      // nothing left to wait for. Deferring again would re-arm the pending state from inside the
      // very drain that is applying it, and the frequency this dimension was left on would be
      // dropped without a word — the world is primed once and never retried. Resolve it as manual
      // instead, which restores that frequency below.
      effectiveSourceKey = MANUAL_SOURCE_KEY;
      effectiveSlotId = MANUAL_SOURCE_KEY;
    }

    if (effectiveSourceKey === MANUAL_SOURCE_KEY || (!hasSources && effectiveSourceKey === null)) {
      if (!hasSources && !this._sourcesInitialized) {
        // Defer manual/default application until sources initialize
        this._pendingSourceState = { source: sourceState, frequency: storedFrequency };
        return false;
      }
      debugLog(this, '_applyStoredSourceState -> manual', {
        effectiveSourceKey,
        storedFrequency,
        baseFrequency,
      });
      this.setFrequencySource(MANUAL_SOURCE_KEY);
      const freq = Number.isFinite(storedFrequency)
        ? storedFrequency
        : Number.isFinite(baseFrequency)
        ? baseFrequency
        : this.baseFrequency;
      if (Number.isFinite(freq)) {
        this.setBaseFrequency(freq, {
          skipSourceAssignment: true,
          skipMultiplierReset: false,
          skipSourceBaseUpdate: false,
        });
      }
      this.currentMultiplier = Number.isFinite(multiplier) ? multiplier : 1;
      return true;
    }

    if (effectiveSourceKey === null) {
      debugLog(this, '_applyStoredSourceState -> promoteFallback', {
        storedFrequency,
        baseFrequency,
      });
      const fallbackSlot =
        Object.keys(this.frequencySlotMap || {}).find(
          (key) => key !== MANUAL_SOURCE_KEY && this.frequencySlotMap[key] === 'minimumCosmicLfo',
        ) ||
        Object.keys(this.frequencySlotMap || {}).find((key) => key !== MANUAL_SOURCE_KEY);

      if (fallbackSlot) {
        this.setFrequencySource(fallbackSlot);
        return true;
      }

      this.currentMultiplier = 1;
      return true;
    }

    const selection = effectiveSourceKey || effectiveSlotId;
    debugLog(this, '_applyStoredSourceState -> cosmic', {
      selection,
      effectiveSourceKey,
      effectiveSlotId,
      baseFrequency,
      multiplier,
      storedFrequency,
    });
    this.setFrequencySource(selection);

    if (Number.isFinite(baseFrequency)) {
      this.sourceBaseFrequency = baseFrequency;
      this.setBaseFrequency(baseFrequency, {
        skipSourceAssignment: true,
        skipMultiplierReset: true,
        skipSourceBaseUpdate: true,
      });
    }

    const hasMultiplier = Number.isFinite(multiplier);
    this.currentMultiplier = 1;

    if (hasMultiplier && multiplier !== 1) {
      this.applyTriggerMultiplier(multiplier);
    } else if (hasMultiplier) {
      this.currentMultiplier = multiplier;
    }

    const finalFrequency = Number.isFinite(storedFrequency)
      ? storedFrequency
      : Number.isFinite(baseFrequency) && hasMultiplier
        ? this._sanitizeFrequency(baseFrequency * multiplier)
        : null;

    if (Number.isFinite(finalFrequency)) {
      this.setBaseFrequency(finalFrequency, {
        skipSourceAssignment: true,
        skipMultiplierReset: true,
        skipSourceBaseUpdate: true,
      });
    }

    return true;
  }

  _applyScopedStateFromRegistry() {
    const { stackId, dimensionId } = this.getScopedContext();
    if (!stackId || !dimensionId) return;
    const ids = this._getComponentIds();

    let storedToggle;
    let storedWaveform;
    let storedAmplitude;
    let storedFrequency;
    let storedSource;
    try {
      storedToggle = getScopedState(stackId, ids.toggle, { dimensionId });
    } catch (_) {}
    try {
      storedWaveform = getScopedState(stackId, ids.waveform, { dimensionId });
    } catch (_) {}
    try {
      storedAmplitude = getScopedState(stackId, ids.amplitude, { dimensionId });
    } catch (_) {}
    try {
      storedFrequency = getScopedState(stackId, ids.frequency, { dimensionId });
    } catch (_) {}
    try {
      storedSource = getScopedState(stackId, ids.source, { dimensionId });
    } catch (_) {}
    debugLog(this, '_applyScopedStateFromRegistry:stored', {
      dimensionId,
      storedToggle,
      storedWaveform,
      storedAmplitude,
      storedFrequency,
      storedSource,
    });

    // Avoid forcing blueprint defaults (near MIN_FREQUENCY_HZ) before sources are ready
    if (!storedSource && Number.isFinite(storedFrequency) && Math.abs(storedFrequency - MIN_FREQUENCY_HZ) <= MIN_FREQUENCY_HZ * 0.001 && !this._sourcesInitialized) {
      this._pendingSourceState = { source: null, frequency: storedFrequency };
      debugLog(this, '_applyScopedStateFromRegistry:deferredDefault', {
        reason: 'sources-not-initialized',
        storedFrequency,
      });
      return;
    }

    this._duringScopedApply = true;
    try {
      // Get or create dimension LFO state
      const dimensionLFO = this._ensureDimensionState(dimensionId);

      if (storedWaveform) {
        this.setWaveform(storedWaveform);
      } else if (dimensionLFO?.waveform) {
        // Sync UI with dimension state
        this.waveform = dimensionLFO.waveform;
        this._currentWaveformSelection = `${this.axis}-waveform-${dimensionLFO.waveform}`;
      }

      if (Number.isFinite(storedAmplitude)) {
        this.setAmplitude(storedAmplitude);
      } else if (dimensionLFO && Number.isFinite(dimensionLFO.amplitude)) {
        // Sync UI with dimension state
        this.amplitude = dimensionLFO.amplitude;
      }

      const sourceApplied = this._applyStoredSourceState(storedSource, storedFrequency);
      if (!sourceApplied) {
        // Pending until sources are available.
      } else {
        this._pendingSourceState = null;
        if (!storedSource && Number.isFinite(storedFrequency)) {
          this.setFrequencySource(MANUAL_SOURCE_KEY);
          this.setBaseFrequency(storedFrequency, {
            skipSourceAssignment: true,
            skipMultiplierReset: false,
            skipSourceBaseUpdate: false,
          });
        } else if (dimensionLFO) {
          // Restore dimension-specific source settings to shared UI state
          this.currentFrequencySource = dimensionLFO.currentFrequencySource || MANUAL_SOURCE_KEY;
          this.currentExoplanet = dimensionLFO.currentExoplanet || MANUAL_SOURCE_KEY;
          this._syncTempoFollow(); // this dimension may not be the tempo-locked one
          this.currentMultiplier = Number.isFinite(dimensionLFO.currentMultiplier) 
            ? dimensionLFO.currentMultiplier 
            : 1;
          this.sourceBaseFrequency = Number.isFinite(dimensionLFO.sourceBaseFrequency)
            ? dimensionLFO.sourceBaseFrequency
            : this.baseFrequency;
          
          // Compute final frequency from base * multiplier — EXCEPT when this dimension is
          // tempo-locked, where the frequency is derived from the deck and `sourceBaseFrequency`
          // still holds whatever the previous source left there. Multiplying that stale base would
          // overwrite the rate we just restored, and the LFO would run at the old source's speed
          // until the next tempo change happened to correct it.
          if (this._isTempoLocked()) {
            this._applyNoteRateFrequency();
          } else if (Number.isFinite(dimensionLFO.sourceBaseFrequency) && Number.isFinite(dimensionLFO.currentMultiplier)) {
            const computedFrequency = this._sanitizeFrequency(
              dimensionLFO.sourceBaseFrequency * dimensionLFO.currentMultiplier
            );
            this.baseFrequency = computedFrequency;
            dimensionLFO.frequency = computedFrequency;
          } else if (Number.isFinite(dimensionLFO.frequency)) {
            this.baseFrequency = dimensionLFO.frequency;
          }
        }
      }
    } finally {
      this._duringScopedApply = false;
    }

    this._persistCosmicState();

    // The dimension this LFO's in-memory speed and depth now describe. `publishSurfaceToParams()`
    // reads it to refuse writing this surface onto a DIFFERENT dimension.
    this._surfaceDimensionId = dimensionId;

    // Cosmic surface: publish the restored frequency to the PM param so the React freq
    // knob + monitor track a dimension switch (one targeted publish for the now-active dim;
    // tagged → self-guarded, never re-drives the LFO). The per-call write-back inside
    // setBaseFrequency stays suppressed during restore (`_duringScopedApply`) so this is the
    // single, final value that lands.
    this._publishFrequencyToParam(this.baseFrequency, dimensionId);
    this._publishAmplitudeToParam(this.amplitude, dimensionId);
    // React cosmic surface re-reads enable / source / waveform for the now-active dimension.
    this._notifyCosmicChange();
  }

  /**
   * Starts the LFO oscillation loop for the current dimension.
   * @param {{ roomRemote?: boolean }} [options] `roomRemote` marks a peer's switch-on being applied.
   */
  start({ roomRemote = false } = {}) {
    if (this._roomRefuses(roomRemote)) {
      this._notifyCosmicChange(); // correct an optimistic React toggle (the room said no)
      return;
    }
    const { dimensionId } = this.getScopedContext();
    if (!dimensionId) {
      console.warn(`[CosmicLFO:${this.axis}] Cannot start: no dimensionId`);
      this._notifyCosmicChange(); // correct an optimistic React toggle (nothing was enabled)
      return;
    }
    const dimensionLFO = this._ensureDimensionState(dimensionId);
    if (!dimensionLFO) {
      this._notifyCosmicChange();
      return;
    }

    dimensionLFO.isActive = true;
    // Don't reset phase to 0 - preserve continuity on restart
    if (dimensionLFO.frequency == null) {
      dimensionLFO.frequency = this.baseFrequency;
    }
    if (!dimensionLFO.waveform) {
      dimensionLFO.waveform = this.waveform;
    }
    if (dimensionLFO.amplitude == null) {
      dimensionLFO.amplitude = this.amplitude;
    }
    this._activeDimensions.add(dimensionId);
    this.isActive = this._activeDimensions.size > 0;
    // PER-DIMENSION FIX: persist the enable toggle for THIS dimension so a dimension
    // switch-away-and-back restores it (the React toggle drives start()/stop() directly, so
    // without this the per-dim enable was never written and the restore stopped the channel).
    // Skipped during a scoped restore (it's reading FROM the registry, not changing intent).
    if (!this._duringScopedApply) {
      this._persistToggleState(true);
    }

    // Modulation flows through ParameterManager updates rather than direct audio connections
    this._ensureSamplingLoop({ forceInitial: true });
    this._notifyCosmicChange(); // React cosmic-enable toggle re-reads
    this._shareRoomAct(roomRemote, 'publishEnable', true);

    //console.log(`[CosmicLFO:${this.axis}] Started for dimension: ${dimensionId}`);
  }

  /**
   * Stops the LFO oscillation loop for the current dimension.
   * @param {{ roomRemote?: boolean }} [options] `roomRemote` marks a peer's switch-off being applied.
   */
  stop({ roomRemote = false } = {}) {
    if (this._roomRefuses(roomRemote)) {
      this._notifyCosmicChange();
      return;
    }
    const { dimensionId } = this.getScopedContext();
    if (!dimensionId) {
      console.warn(`[CosmicLFO:${this.axis}] Cannot stop: no dimensionId`);
      this._notifyCosmicChange(); // correct an optimistic React toggle
      return;
    }
    const dimensionLFO = this.dimensionLFOs.get(dimensionId);
    if (!dimensionLFO || !dimensionLFO.isActive) {
      this._activeDimensions.delete(dimensionId);
      this.isActive = this._activeDimensions.size > 0;
      this._stopSamplingLoopIfIdle();
      if (!this._duringScopedApply) {
        this._persistToggleState(false);
      }
      this._notifyCosmicChange();
      // Deliberately NOT published: there was nothing running to switch off, so this is not a press.
      // Teardown reaches here too (stopping an LFO that never started), and a teardown that
      // disabled every other player's LFO would be a bad way to find that out. A peer whose picture
      // of this deck disagrees is corrected by the snapshot, which is what the snapshot is for.
      return;
    }
    dimensionLFO.isActive = false;
    this._activeDimensions.delete(dimensionId);
    this.isActive = this._activeDimensions.size > 0;
    this._stopSamplingLoopIfIdle();
    // PER-DIMENSION FIX: persist the disabled state for THIS dimension (see start()).
    if (!this._duringScopedApply) {
      this._persistToggleState(false);
    }
    this._notifyCosmicChange(); // React cosmic-enable toggle re-reads
    this._shareRoomAct(roomRemote, 'publishEnable', false);

    // No audio connections - only parameter updates
    this._lastPushedParamValue = null;
    
    //console.log(`[CosmicLFO:${this.axis}] Stopped for dimension: ${dimensionId}`);
  }

  /**
   * REMOVED: bindAudioParam() - No longer needed.
   * LFOs now only control root parameters via parameter updates, not audio connections.
   */

  /**
   * Creates a new dimension-specific oscillator state.
   * @param {string} dimensionId - The dimension to create the state for
   * @returns {object} The dimension state record
   */
  _createDimensionState(dimensionId) {
    // Initialize with current shared state instead of hardcoded defaults
    // This ensures new dimensions inherit the current cosmic source settings
    const dimensionLFO = {
      isActive: false,
      phase: 0,
      frequency: this.baseFrequency,
      amplitude: this.amplitude,
      waveform: this.waveform,
      // Copy current frequency source settings from shared state
      currentFrequencySource: this.currentFrequencySource || MANUAL_SOURCE_KEY,
      currentExoplanet: this.currentExoplanet || MANUAL_SOURCE_KEY,
      currentMultiplier: this.currentMultiplier || 1,
      sourceBaseFrequency: this.sourceBaseFrequency || this.baseFrequency,
      currentNoteRate: this.getNoteRate(),
    };
    
    this.dimensionLFOs.set(dimensionId, dimensionLFO);
    //console.log(`[CosmicLFO:${this.axis}] Created dimension state for: ${dimensionId} with source: ${dimensionLFO.currentFrequencySource}`);
    
    return dimensionLFO;
  }

  _ensureDimensionState(dimensionId = null) {
    const targetId = dimensionId ?? this.getScopedContext().dimensionId;
    if (!targetId) {
      console.warn(`[CosmicLFO:${this.axis}] Cannot ensure dimension state: no dimensionId`);
      return null;
    }

    let dimensionLFO = this.dimensionLFOs.get(targetId);
    if (!dimensionLFO) {
      dimensionLFO = this._createDimensionState(targetId);
    }

    return dimensionLFO;
  }

  _getParameterRange() {
    const param = this.parameterManager?.getParameter(this.axis);
    if (!param) {
      return { min: 0, max: 1 };
    }
    if (param.min === param.max) {
      const fallback = param.min || 0;
      return { min: fallback, max: fallback + 1 };
    }
    return { min: param.min, max: param.max };
  }

  _ensureSamplingLoop({ forceInitial = false } = {}) {
    if (this._visualSamplerActive) {
      if (forceInitial) {
        this._tickSampler();
      }
      return;
    }

    this._visualSamplerActive = true;
    this._lastVisualTimestamp = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    this._tickSampler();
    this._scheduleNextSample();
  }

  _stopSamplingLoopIfIdle() {
    if (this._activeDimensions.size === 0) {
      this._visualSamplerActive = false;
      this._cancelScheduledSample();
      this._lastVisualTimestamp = null;
    }
  }

  _isDocumentVisible() {
    if (typeof document === 'undefined') {
      return true;
    }
    return document.visibilityState !== 'hidden';
  }

  _getBackgroundTickMs() {
    const sampleRate = Number(this.visualSampleRate);
    if (!Number.isFinite(sampleRate) || sampleRate <= 0) {
      return 33;
    }
    return Math.max(16, Math.round(1000 / Math.max(1, Math.min(sampleRate, 60))));
  }

  _cancelScheduledSample() {
    if (!this._samplerHandle) {
      return;
    }
    if (this._samplerMode === 'timeout') {
      clearTimeout(this._samplerHandle);
    } else {
      cancelAnimationFrame(this._samplerHandle);
    }
    this._samplerHandle = null;
    this._samplerMode = null;
  }

  _scheduleNextSample() {
    if (!this._visualSamplerActive) {
      return;
    }

    if (this._isDocumentVisible() && typeof requestAnimationFrame === 'function') {
      this._samplerMode = 'raf';
      this._samplerHandle = requestAnimationFrame((now) => {
        this._samplerHandle = null;
        if (!this._visualSamplerActive) {
          return;
        }
        this._tickSampler({ rafNow: now });
        this._scheduleNextSample();
      });
      return;
    }

    this._samplerMode = 'timeout';
    this._samplerHandle = setTimeout(() => {
      this._samplerHandle = null;
      if (!this._visualSamplerActive) {
        return;
      }
      this._tickSampler();
      this._scheduleNextSample();
    }, this._getBackgroundTickMs());
  }

  _onVisibilityChange() {
    if (!this._visualSamplerActive || this._activeDimensions.size === 0) {
      return;
    }

    this._cancelScheduledSample();
    this._tickSampler();
    this._scheduleNextSample();
  }

  _tickSampler({ rafNow = null } = {}) {
    if (this._activeDimensions.size === 0) {
      return;
    }
    // Use the rAF-provided high-res timestamp when available to avoid a
    // redundant performance.now() call and keep deltaTime accurate.
    const now = rafNow ?? (typeof performance !== 'undefined' ? performance.now() : Date.now());
    let deltaTime = null;
    if (this._lastVisualTimestamp != null) {
      deltaTime = Math.max((now - this._lastVisualTimestamp) / 1000, 0);
    }
    this._lastVisualTimestamp = now;
    this._syncParameterFromLfo({ deltaTime });
  }

  _syncParameterFromLfo({ deltaTime = null } = {}) {
    // In a room only ONE client's LFO drives the axis (see setRoomBridge). The others hold the same
    // settings and take the values off the wire, so one deck modulates along one sweep rather than
    // as many as there are players. Nothing is advanced here either — this client's private phase
    // would otherwise drift over the values coming from someone else's oscillator. The role
    // is re-derived whenever grants change, and whoever inherits it simply resumes from the phase
    // it was left on.
    if (!this._roomGenerates()) return;

    // Early exit if we don't have parameterManager
    if (!this.parameterManager) {
      console.warn(`[CosmicLFO:${this.axis}] No parameterManager!`);
      return;
    }
    
    //console.log(`[CosmicLFO:${this.axis}] _syncParameterFromLfo called, dimensionLFOs size: ${this.dimensionLFOs.size}`);

    // Update ALL active dimension LFOs and push their values
    this.dimensionLFOs.forEach((dimensionLFO, dimensionId) => {
      //console.log(`[CosmicLFO:${this.axis}:${dimensionId}] Checking - isActive: ${dimensionLFO.isActive}`);

      if (!dimensionLFO.isActive) {
        return;
      }

      const range = this._getParameterRange();
      const amplitude = dimensionLFO.amplitude ?? this.amplitude;
      const step = Number.isFinite(deltaTime) && deltaTime > 0 ? deltaTime : 1 / 60;
      const frequency = Math.max(dimensionLFO.frequency ?? this.baseFrequency ?? MIN_FREQUENCY_HZ, MIN_FREQUENCY_HZ);
      const waveformType = dimensionLFO.waveform ?? this.waveform;
      // One pure oscillator step (advance phase, evaluate waveform, map +
      // clamp into the param range). Feed the advanced phase back for the next tick.
      const { phase, value: clamped } = oscSample({
        phase: dimensionLFO.phase,
        frequencyHz: frequency,
        dtSeconds: step,
        waveform: waveformType,
        amplitude,
        range,
      });
      dimensionLFO.phase = phase;
      //console.log(`[CosmicLFO:${this.axis}:${dimensionId}] LFO value: ${clamped}, freq: ${frequency}, waveform=${waveformType}, range: [${range.min}, ${range.max}]`);
      
      // Push the value to the root parameter for this dimension
      // (ParameterManager will reject if dimension is locked)
      this._pushRootParameterValue(clamped, dimensionId);
    });
  }

  /**
   * Pushes the LFO value to the root parameter for a specific dimension.
   * This is what actually makes the audio change!
   */
  _pushRootParameterValue(value, dimensionId) {
    // parameterManager and stackId are already validated in _syncParameterFromLfo
    // Push through the unified seam. Priority now from PRIORITY_MAP (cosmic-x/y/z),
    // which keeps cosmic below sensors (the intended order), replacing the hard-coded 10.
    const inputSource = this._inputSource();
    if (!inputSource) return;
    inputSource.set(this.axis, value, { kind: 'raw', dim: dimensionId });
  }

  /**
   * Sets the waveform type (sine, square, triangle, etc.).
   * @param {string} newWaveform - The desired waveform type.
   * @param {{ roomRemote?: boolean }} [options] `roomRemote` marks a peer's pick being applied.
   */
  setWaveform(newWaveform, { roomRemote = false } = {}) {
    if (this._roomRefuses(roomRemote)) {
      this._notifyCosmicChange(); // correct the optimistic React select
      return;
    }
    const selectionValue = typeof newWaveform === 'string' && newWaveform.includes('-waveform-')
      ? newWaveform
      : `${this.axis}-waveform-${newWaveform || this.waveform}`;
    const extracted = selectionValue.replace(`${this.axis}-waveform-`, '').toLowerCase();
    const resolved = ALLOWED_WAVEFORMS.includes(extracted) ? extracted : 'sine';
    this.waveform = resolved;
    this._currentWaveformSelection = selectionValue;
    
    const { dimensionId } = this.getScopedContext();
    if (dimensionId) {
      const dimensionLFO = this._ensureDimensionState(dimensionId);
      if (dimensionLFO) {
        dimensionLFO.waveform = resolved;
      }
    }
    
    this._syncParameterFromLfo();

    if (!this._duringScopedApply) {
      this._persistCosmicState({ waveform: selectionValue });
    }
    this._notifyCosmicChange(); // React waveform select re-reads
    // The RESOLVED key, never the raw selection: both ends must land on the same shape even when
    // one of them was handed a waveform it does not know (which falls back to sine here).
    this._shareRoomAct(roomRemote, 'publishWaveform', resolved);
  }

  /**
   * Sets the base frequency.
   * @param {number} freq - The new frequency.
   */
  /**
   * Cosmic surface — bind the CosmicLFO ↔ `<axis>-cosmic-frequency` PM bridge once
   * the param exists (registered by OrbitersEditMode). Idempotent + guarded on registration,
   * so it's safe to call from several lifecycle points (enterMode / setBaseFrequency) and only
   * binds when the param is live. Subscribes with `dimensionId=null` so it follows the active
   * dimension (Option C); inbound edits drive {@link setBaseFrequency}, our own write-back is
   * self-guarded by `_freqParamSource`.
   * @private
   */
  _ensureFrequencyParamBridge() {
    if (this._freqParamBound || !this.parameterManager) return;
    const paramId = cosmicFrequencyParamId(this.axis);
    // Only bind once the param is registered — else this no-ops and a later call retries.
    if (
      typeof this.parameterManager.getParameter === 'function' &&
      !this.parameterManager.getParameter(paramId)
    ) {
      return;
    }
    if (typeof this.parameterManager.subscribe !== 'function') return;
    this._freqParamController = {
      onParameterChanged: (_name, value, dimensionId, metadata) => {
        this._onFrequencyParamChanged(value, dimensionId, metadata);
      },
    };
    this.parameterManager.subscribe(
      this._freqParamController,
      paramId,
      getPriority(`cosmic-${this.axis}`),
      null,
    );
    this._freqParamBound = true;
  }

  /**
   * Inbound from the React manual-freq knob / MIDI: apply a frequency edit to the LFO.
   * Ignores our own write-back (`sourceController === _freqParamSource`) and anything other
   * than a genuine value-change / unchanged-commit (dimension restore is handled by
   * `_applyScopedStateFromRegistry`, not re-driven from notifications).
   * @private
   */
  _onFrequencyParamChanged(value, _dimensionId, metadata) {
    if (metadata?.sourceController === this._freqParamSource) return;
    const reason = metadata?.reason;
    if (reason !== 'value-change' && reason !== 'unchanged-commit') return;
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return;
    // Treat as a manual frequency edit (the knob only shows in manual mode) — mirror the
    // legacy WAC knob change handler's options (keep the source, reset the multiplier, update
    // the source base). Flag so setBaseFrequency does not re-publish the value we just received.
    this._applyingFreqFromParam = true;
    try {
      this.setBaseFrequency(numeric, {
        skipSourceAssignment: true,
        skipMultiplierReset: false,
        skipSourceBaseUpdate: false,
      });
    } finally {
      this._applyingFreqFromParam = false;
    }
  }

  /**
   * Write-back: publish an LFO-internal frequency change (kick, source switch, manual knob)
   * to the `<axis>-cosmic-frequency` PM param so the React knob + monitor reflect it. Tagged
   * with `_freqParamSource` so our own subscription ignores it. No-op when the param isn't
   * registered or no dimension is resolvable.
   * @private
   */
  _publishFrequencyToParam(value, dimensionId) {
    this._publishToParam(cosmicFrequencyParamId(this.axis), value, dimensionId, this._freqParamSource);
  }

  /**
   * The one write-back path behind {@link _publishFrequencyToParam} and
   * {@link _publishAmplitudeToParam}: put `value` on `paramId` for `dimensionId`, tagged with
   * `sourceTag` so this LFO's own subscription ignores it.
   *
   * Skips quietly when the parameter is not registered — which at boot simply means registration
   * has not happened yet, not that anything is wrong. Nothing is lost by waiting:
   * {@link publishSurfaceToParams} re-publishes once it has.
   * @private
   */
  _publishToParam(paramId, value, dimensionId, sourceTag) {
    const pm = this.parameterManager;
    if (!pm) return;
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return;
    // A PM that cannot be inspected is assumed usable — the setters below are the real test.
    const param = typeof pm.getParameter === 'function' ? pm.getParameter(paramId) : null;
    if (typeof pm.getParameter === 'function' && !param) return;
    const dim = dimensionId || this.getScopedContext().dimensionId;
    const priority = getPriority(`cosmic-${this.axis}`);
    const options = { updateIntent: 'commit' };
    const perDimension =
      dim && param?.isMultidimensional !== false && typeof pm.setDimensionValue === 'function';
    try {
      if (perDimension) {
        pm.setDimensionValue(paramId, dim, numeric, sourceTag, priority, options);
        return;
      }
      // The parameter is registered but there is no dimension to write to, or it is registered
      // FLAT. Write the flat value rather than dropping it: that value is what the knob reads, so
      // skipping would leave the readout on the registration default while the LFO ran at another
      // number — the very thing this publish exists to correct.
      if (typeof pm.setRawValue === 'function') {
        pm.setRawValue(paramId, numeric, sourceTag, priority, options);
      }
    } catch (_error) {
      // Tolerate a locked dimension / mid-teardown PM — the knob just won't track this change.
    }
  }

  /**
   * Cosmic surface — broadcast a non-PM cosmic state change (enable / source /
   * waveform) so the React `cosmic` surface re-reads it. Freq + amplitude flow through their
   * PM params (the knobs auto-update), so this is only for the discrete/boolean controls.
   * @private
   */
  _notifyCosmicChange() {
    // Dispatch on this voice's eventBus (window for single-orbiter → byte-identical).
    if (!this._eventBus || typeof this._eventBus.dispatchEvent !== 'function') return;
    try {
      this._eventBus.dispatchEvent(new CustomEvent('orbiters:cosmic-changed', { detail: { axis: this.axis } }));
    } catch (_error) {
      // no-op
    }
  }

  /** Whether the cosmic LFO is enabled (running) for the ACTIVE dimension (React toggle). */
  isCosmicEnabled() {
    const { dimensionId } = this.getScopedContext();
    if (!dimensionId) return Boolean(this.isActive);
    const dim = this.dimensionLFOs.get(dimensionId);
    return dim ? Boolean(dim.isActive) : false;
  }

  /** The current modulation-source key (catalog key, e.g. 'manual' / 'minimumCosmicLfo'). */
  getFrequencySource() {
    return this.currentFrequencySource || MANUAL_SOURCE_KEY;
  }

  /**
   * The source keys that can actually be PICKED right now, in catalog order — the one owner of
   * that rule, so the menu cannot offer a source this LFO would then refuse.
   *
   * A discrete source only appears when the world supplies a value for it. The manual knob and the
   * tempo lock always appear: neither reads world data, one is the user's own number and the other
   * comes from the deck. Before world data arrives (`_sourcesInitialized` false) the discrete
   * sources are unknown rather than absent, so the full catalog is offered and the menu settles
   * once the world seeds.
   */
  getAvailableSourceKeys() {
    if (!this._sourcesInitialized) {
      return COSMIC_SOURCES.map((s) => s.key);
    }
    return COSMIC_SOURCES
      .map((s) => s.key)
      .filter((key) => key === MANUAL_SOURCE_KEY
        || key === SUBDIVISION_SOURCE_KEY
        || typeof this.frequencySources?.[key] === 'number');
  }

  /** The current waveform key (e.g. 'sine'). */
  getWaveform() {
    return this.waveform || 'sine';
  }

  setBaseFrequency(freq, options = {}) {
    this._ensureFrequencyParamBridge();
    const {
      skipSourceAssignment = false,
      skipMultiplierReset = false,
      skipSourceBaseUpdate = false
    } = options;

    const sanitized = this._sanitizeFrequency(freq);
    debugLog(this, 'setBaseFrequency', {
      requested: freq,
      sanitized,
      options,
    });
    this.baseFrequency = sanitized;

    const { dimensionId } = this.getScopedContext();
    const dimensionLFO = dimensionId ? this._ensureDimensionState(dimensionId) : null;

    if (dimensionLFO) {
      dimensionLFO.frequency = sanitized;
      
      if (!skipMultiplierReset) {
        dimensionLFO.currentMultiplier = 1;
      }

      if (!skipSourceBaseUpdate) {
        dimensionLFO.sourceBaseFrequency = sanitized;
      }

      if (!skipSourceAssignment) {
        dimensionLFO.currentFrequencySource = MANUAL_SOURCE_KEY;
        dimensionLFO.currentExoplanet = MANUAL_SOURCE_KEY;
      }
    }

    // Also update shared state for UI display
    if (!skipMultiplierReset) {
      this.currentMultiplier = 1;
    }

    if (!skipSourceBaseUpdate) {
      this.sourceBaseFrequency = sanitized;
    }

    if (!skipSourceAssignment) {
      // A frequency written without naming a source IS a manual frequency — so this silently ends a
      // tempo lock, and the deck subscription has to end with it.
      this.currentFrequencySource = MANUAL_SOURCE_KEY;
      this.currentExoplanet = MANUAL_SOURCE_KEY;
      this._syncTempoFollow();
    }

    this._syncParameterFromLfo();
    if (!this._duringScopedApply) {
      this._persistCosmicState({ baseFrequency: this.baseFrequency });
    }
    // Cosmic surface: mirror the new frequency to the PM param so the React knob +
    // monitor track kick / source-switch / manual changes. Skip when we're applying a value
    // that just came FROM the param (no echo) or during a scoped restore (handled elsewhere).
    if (!this._applyingFreqFromParam && !this._duringScopedApply) {
      this._publishFrequencyToParam(sanitized, dimensionId);
    }
  }

  /**
   * Ensures amplitude controls are initialized and bound.
   * @private
   */

  /**
   * Shows or hides the amplitude controls.
   * @param {boolean} show - Whether to show the controls.
   * @private
   */

  /**
   * Syncs the visual knob state with the internal amplitude value.
   * @private
   */

  /** The dash-form PM param id the React amplitude knob binds to. @private */
  _amplitudeParamId() {
    return `${this.axis}-cosmic-amplitude`;
  }

  /**
   * Cosmic surface — bind the CosmicLFO ↔ `<axis>-cosmic-amplitude` PM bridge (mirror
   * of the frequency bridge). Idempotent + guarded on registration. @private
   */
  _ensureAmplitudeParamBridge() {
    if (this._ampParamBound || !this.parameterManager) return;
    const paramId = this._amplitudeParamId();
    if (
      typeof this.parameterManager.getParameter === 'function' &&
      !this.parameterManager.getParameter(paramId)
    ) {
      return;
    }
    if (typeof this.parameterManager.subscribe !== 'function') return;
    this._ampParamController = {
      onParameterChanged: (_name, value, dimensionId, metadata) => {
        this._onAmplitudeParamChanged(value, dimensionId, metadata);
      },
    };
    this.parameterManager.subscribe(
      this._ampParamController,
      paramId,
      getPriority(`cosmic-${this.axis}`),
      null,
    );
    this._ampParamBound = true;
  }

  /** Inbound from the React amplitude knob / MIDI → apply to the LFO. @private */
  _onAmplitudeParamChanged(value, _dimensionId, metadata) {
    if (metadata?.sourceController === this._ampParamSource) return;
    const reason = metadata?.reason;
    if (reason !== 'value-change' && reason !== 'unchanged-commit') return;
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return;
    this._applyingAmpFromParam = true;
    try {
      this.setAmplitude(numeric);
    } finally {
      this._applyingAmpFromParam = false;
    }
  }

  /** Write-back: mirror an LFO-internal amplitude change to the PM param. @private */
  _publishAmplitudeToParam(value, dimensionId) {
    this._publishToParam(this._amplitudeParamId(), value, dimensionId, this._ampParamSource);
  }

  /**
   * Push the LFO's current speed and depth into their ParameterManager params for the active
   * dimension, so the knobs and monitors show what the LFO is actually running at.
   *
   * The restore that runs at boot publishes these once, and at that moment the params exist only
   * as the flat placeholder a subscribing control auto-creates — a per-dimension write is refused
   * against a flat param, so that first publish is dropped and the readout keeps the registration
   * default for the rest of the session while the LFO runs at the restored value. Call this once
   * the params have been registered as multidimensional to close that window.
   */
  publishSurfaceToParams() {
    const { dimensionId } = this.getScopedContext();
    if (!dimensionId) return;
    // Only publish a surface that actually belongs to this dimension. A caller can re-register the
    // parameters after the active dimension moved but BEFORE this LFO re-read its state for the new
    // one — loading a second orbiter whose saved selection names a different dimension does exactly
    // that, assigning `activeDimensionId` without the dimension-changed event this LFO listens for.
    // Publishing then would stamp the previous dimension's speed and depth onto the new dimension.
    // Skipping is safe: the re-read that follows publishes the right values itself.
    if (this._surfaceDimensionId !== dimensionId) return;
    this._publishFrequencyToParam(this.baseFrequency, dimensionId);
    this._publishAmplitudeToParam(this.amplitude, dimensionId);
  }

  setAmplitude(value, options = {}) {
    this._ensureAmplitudeParamBridge();
    const numeric = Number(value);
    const clamped = Math.max(0, Math.min(1, Number.isFinite(numeric) ? numeric : 1));
    this.amplitude = Number(clamped.toFixed(PARAM_INTERNAL_PRECISION_DECIMALS));
    
    const { dimensionId } = this.getScopedContext();
    if (dimensionId) {
      const dimensionLFO = this._ensureDimensionState(dimensionId);
      if (dimensionLFO) {
        dimensionLFO.amplitude = this.amplitude;
      }
    }
    
    // Sync UI

    this._syncParameterFromLfo();

    if (!this._duringScopedApply) {
      this._persistCosmicState({ amplitude: this.amplitude });
    }
    // Cosmic surface: mirror the amplitude to the PM param so the React knob tracks it
    // (skip echo from a param-driven apply + per-call writes during a scoped restore — the
    // restore's single targeted publish lands the final value).
    if (!this._applyingAmpFromParam && !this._duringScopedApply) {
      this._publishAmplitudeToParam(this.amplitude, this.getScopedContext().dimensionId);
    }
  }

  isAudioConnected() {
    return false;
  }

  // Clamp + round delegates to the pure FrequencySourceManager kernel.
  _sanitizeFrequency(freq) {
    return sanitizeFrequency(freq);
  }

  /**
   * Called when the Cosmic LFO mode is activated.
   */
  enterMode() {
    // Cosmic surface: bind the freq + amplitude PM bridges so the React knobs can drive
    // the LFO the moment the cosmic panel is active (idempotent; only bind once params exist).
    this._ensureFrequencyParamBridge();
    this._ensureAmplitudeParamBridge();

    const { dimensionId } = this.getScopedContext();
    const dimensionLFO = dimensionId ? this.dimensionLFOs.get(dimensionId) : null;
    if (dimensionLFO) {
      this.currentFrequencySource = dimensionLFO.currentFrequencySource || MANUAL_SOURCE_KEY;
      this.currentExoplanet = dimensionLFO.currentExoplanet || MANUAL_SOURCE_KEY;
      this._syncTempoFollow();
      this.currentMultiplier = Number.isFinite(dimensionLFO.currentMultiplier)
        ? Number(dimensionLFO.currentMultiplier)
        : 1;
      this.sourceBaseFrequency = Number.isFinite(dimensionLFO.sourceBaseFrequency)
        ? dimensionLFO.sourceBaseFrequency
        : this.baseFrequency;
      if (Number.isFinite(dimensionLFO.frequency)) {
        this.baseFrequency = dimensionLFO.frequency;
      }
    }

    // VIEW-ONLY: do NOT start/stop the LFO on a panel switch. The channel enable is owned by the React
    // cosmic surface (toggle → cosmic.setEnabled → start/stop) + per-dimension scoped state. The legacy
    // `${axis}CosmicLFO` <webaudio-switch> was removed in the React migration, so the old getElementById
    // guard always fell through to stop() and silently disabled the LFO every time the panel changed.
  }

  /** Optional: If you need exoplanet-based logic **/
  handleSelectionChange(type, value) {
    if (type === 'waveform') {
      this.setWaveform(value);
    } else if (type === 'exo') {
      this.setCurrentExoplanet(value);
    }
  }
  
  setCurrentExoplanet(selection) {
    this.setFrequencySource(selection);
  }

  /**
   * Pick the modulation SOURCE — which cosmic body's datum sets the frequency.
   * @param {string} selection - A catalog source key (or the legacy `manual-frequency` alias).
   * @param {{ roomRemote?: boolean }} [options] `roomRemote` marks a peer's pick being applied.
   */
  setFrequencySource(selection, { roomRemote = false } = {}) {
    if (this._roomRefuses(roomRemote)) {
      this._notifyCosmicChange(); // correct the optimistic React select
      return;
    }
    this._setFrequencySourceImpl(selection, { roomRemote });
    // Notify on EVERY path (incl. the fallback-to-manual when a source has no data) so
    // the React source select re-reads and reflects the true source promptly, instead of the
    // optimistic pick lingering (showing the wrong mode) until the next unrelated cosmic event.
    this._notifyCosmicChange();
    // The SETTLED source, not the requested one: a source whose data this world does not carry
    // falls back to manual here, and the room has to agree about which panel mode is showing.
    this._shareRoomAct(roomRemote, 'publishSource', this.getFrequencySource());
  }

  _setFrequencySourceImpl(selection, { roomRemote = false } = {}) {
    if (!selection) {
      return;
    }

    const rawValue = typeof selection === 'string' ? selection.trim() : String(selection).trim();
    if (!rawValue) {
      return;
    }
    const sourceKey = rawValue === 'manual-frequency' ? MANUAL_SOURCE_KEY : rawValue;

    debugLog(this, 'setFrequencySource:request', { selection, normalized: sourceKey });

    const { dimensionId } = this.getScopedContext();
    const dimensionLFO = dimensionId ? this._ensureDimensionState(dimensionId) : null;

    if (sourceKey === SUBDIVISION_SOURCE_KEY) {
      debugLog(this, 'setFrequencySource -> subdivision', { dimensionId, rate: this.getNoteRate() });
      this.currentFrequencySource = SUBDIVISION_SOURCE_KEY;
      this.currentExoplanet = SUBDIVISION_SOURCE_KEY;
      // The rate is the source, so a multiplier from a previous source must not survive into it —
      // otherwise a 1/4 would run at half or double the rate its own label promises.
      this.currentMultiplier = 1;
      if (dimensionLFO) {
        dimensionLFO.currentFrequencySource = SUBDIVISION_SOURCE_KEY;
        dimensionLFO.currentExoplanet = SUBDIVISION_SOURCE_KEY;
        dimensionLFO.currentMultiplier = 1;
        if (isNoteRateKey(dimensionLFO.currentNoteRate)) {
          this.currentNoteRate = dimensionLFO.currentNoteRate;
        } else {
          dimensionLFO.currentNoteRate = this.getNoteRate();
        }
      }
      // Follow the deck BEFORE deriving: a deck with no tempo yet derives nothing now, and the
      // subscription is what turns the first tempo it gets into a frequency.
      this._syncTempoFollow();
      this._applyNoteRateFrequency();
      if (!this._duringScopedApply) {
        this._persistCosmicState({ sourceState: this._createSourceState() });
      }
      return;
    }
    // Any other source owns its own frequency — stop following the deck.
    this._unbindTempoFollow();

    if (sourceKey === MANUAL_SOURCE_KEY) {
      debugLog(this, 'setFrequencySource -> manual', { dimensionId });
      this.currentFrequencySource = MANUAL_SOURCE_KEY;
      this.currentExoplanet = MANUAL_SOURCE_KEY;
      this._syncTempoFollow();
      this.sourceBaseFrequency = this.baseFrequency;
      this.currentMultiplier = 1;

      if (dimensionLFO) {
        dimensionLFO.currentFrequencySource = MANUAL_SOURCE_KEY;
        dimensionLFO.currentExoplanet = MANUAL_SOURCE_KEY;
        dimensionLFO.sourceBaseFrequency = dimensionLFO.frequency || this.baseFrequency;
        dimensionLFO.currentMultiplier = 1;
      }

      if (!this._duringScopedApply) {
        this._persistCosmicState({
          sourceState: this._createSourceState(),
          baseFrequency: this.baseFrequency,
        });
      }
      return;
    }

    let resolvedSourceKey = sourceKey;
    if (resolvedSourceKey !== MANUAL_SOURCE_KEY && typeof this.frequencySources[resolvedSourceKey] !== 'number') {
      const ciMatch = Object.keys(this.frequencySources).find(
        (k) => k.toLowerCase() === String(resolvedSourceKey).toLowerCase(),
      );
      if (ciMatch) resolvedSourceKey = ciMatch;
    }
    if (resolvedSourceKey === MANUAL_SOURCE_KEY) {
      debugLog(this, 'setFrequencySource -> manualSlot', { dimensionId, sourceKey: resolvedSourceKey });
      this.currentFrequencySource = MANUAL_SOURCE_KEY;
      this.currentExoplanet = MANUAL_SOURCE_KEY;
      this.sourceBaseFrequency = this.baseFrequency;
      this.currentMultiplier = 1;

      if (dimensionLFO) {
        dimensionLFO.currentFrequencySource = MANUAL_SOURCE_KEY;
        dimensionLFO.currentExoplanet = MANUAL_SOURCE_KEY;
        dimensionLFO.sourceBaseFrequency = dimensionLFO.frequency || this.baseFrequency;
        dimensionLFO.currentMultiplier = 1;
      }

      if (!this._duringScopedApply) {
        this._persistCosmicState({
          sourceState: this._createSourceState(),
          baseFrequency: this.baseFrequency,
        });
      }
      return;
    }

    const nextFrequency = this.frequencySources[resolvedSourceKey];
    if (typeof nextFrequency !== 'number') {
      debugLog(this, 'setFrequencySource -> missingFrequency', {
        dimensionId,
        sourceKey: resolvedSourceKey,
        selection,
      });
      console.warn(`CosmicLFO (${this.axis}): Missing frequency data for source "${resolvedSourceKey}". Falling back to manual input.`);
      // Only nag the USER when THEY actively pick a source whose data is missing. During the internal
      // per-dimension scoped re-application (`_duringScopedApply`, e.g. on a dimension switch) we fall
      // back to manual silently — toasting on every dimension change is noise, not actionable. A
      // peer's pick (`roomRemote`) is silent for the same reason and a stronger one: it is not this
      // user's action at all, and there is nothing they could do about it.
      if (typeof notifications?.showToast === 'function' && !this._duringScopedApply && !roomRemote) {
        const readableLabel = SOURCE_DEFINITIONS[resolvedSourceKey]?.label || resolvedSourceKey;
        notifications.showToast(`Missing data for ${readableLabel}. frequency enabled.`, 'warning', 4000);
      }
      this.currentFrequencySource = MANUAL_SOURCE_KEY;
      this.currentExoplanet = MANUAL_SOURCE_KEY;

      if (dimensionLFO) {
        dimensionLFO.currentFrequencySource = MANUAL_SOURCE_KEY;
        dimensionLFO.currentExoplanet = MANUAL_SOURCE_KEY;
      }

      if (!this._duringScopedApply) {
        this._persistCosmicState({
          sourceState: this._createSourceState(),
          baseFrequency: this.baseFrequency,
        });
      }
      return;
    }

    this.currentFrequencySource = resolvedSourceKey;
    this.currentExoplanet = resolvedSourceKey;
    this.sourceBaseFrequency = nextFrequency;
    this.currentMultiplier = 1;
    debugLog(this, 'setFrequencySource -> cosmic', {
      dimensionId,
      sourceKey: resolvedSourceKey,
      baseFrequency: nextFrequency,
    });

    if (dimensionLFO) {
      dimensionLFO.currentFrequencySource = resolvedSourceKey;
      dimensionLFO.currentExoplanet = resolvedSourceKey;
      dimensionLFO.sourceBaseFrequency = nextFrequency;
      dimensionLFO.currentMultiplier = 1;
    }

    this.setBaseFrequency(nextFrequency, {
      skipSourceAssignment: true,
      skipMultiplierReset: false,
      skipSourceBaseUpdate: false
    });
    if (!this._duringScopedApply) {
      this._persistCosmicState({
        sourceState: this._createSourceState(),
        baseFrequency: this.baseFrequency,
      });
    }
  }

  /**
   * Configures the available frequency sources from astronomical data.
   * @param {object} rawSources
   */
  setFrequencySources(rawSources = {}) {
    const extracted = this._extractFrequencySources(rawSources);
    this.frequencySources = extracted;
    this._sourcesInitialized = true;
    debugLog(this, 'setFrequencySources', { rawSources, extracted });

    this.frequencySlotMap = deriveFrequencySlotMap(extracted);

    // WHICH source this world lands on, using only what the world actually supplies. The Cosmic
    // LFO is the default and wins whenever the world carries a value for it. If it does not, the
    // first other source the world DOES carry takes the axis: that is still a real astronomical
    // value, where the manual knob would be the user's own number wearing a world's label. Only a
    // world with no astronomical data at all falls through, and that is reported below.
    const defaultSlot = typeof extracted[DEFAULT_COSMIC_SOURCE_KEY] === 'number'
      ? DEFAULT_COSMIC_SOURCE_KEY
      : Object.keys(this.frequencySlotMap).find((key) => key !== MANUAL_SOURCE_KEY) ?? null;

    if (defaultSlot !== DEFAULT_COSMIC_SOURCE_KEY) {
      // Reported, not repaired: nothing is substituted for the missing value. Say only what this
      // layer can actually see — that the payload it was handed carries no usable minimum cosmic
      // LFO. WHY it is missing is not knowable from here: the planet may have been ineligible, or
      // this may be a payload shape the extractor does not understand. Naming a cause would send
      // whoever reads this to the wrong place.
      console.warn(
        `[CosmicLFO:${this.axis}] World payload carries no usable minimum cosmic LFO. `
        + `Falling back to ${defaultSlot ?? 'the manual frequency knob'}. `
        + `Nothing is substituted for the missing value.`,
        { payloadKeys: Object.keys(rawSources ?? {}), usable: Object.keys(extracted) },
      );
    }

    if (defaultSlot) {
      this.currentFrequencySource = defaultSlot;
      this.currentExoplanet = defaultSlot;
      this._syncTempoFollow(); // world data seeding its default ends any tempo lock
      this.sourceBaseFrequency = extracted[defaultSlot];
      this.currentMultiplier = 1;
      debugLog(this, 'setFrequencySources -> default cosmic', {
        source: defaultSlot,
        baseFrequency: this.sourceBaseFrequency,
      });
      this.setBaseFrequency(extracted[defaultSlot], {
        skipSourceAssignment: true,
        skipMultiplierReset: false,
        skipSourceBaseUpdate: false
      });
    } else {
      this.currentFrequencySource = MANUAL_SOURCE_KEY;
      this.currentExoplanet = MANUAL_SOURCE_KEY;
      this._syncTempoFollow(); // world data with no usable default also ends any tempo lock
      this.sourceBaseFrequency = this.baseFrequency;
      this.currentMultiplier = 1;
      debugLog(this, 'setFrequencySources -> default manual', {
        baseFrequency: this.baseFrequency,
      });
    }

    if (this.currentFrequencySource !== MANUAL_SOURCE_KEY && Number.isFinite(this.sourceBaseFrequency)) {
      this._reseedDimensionStatesWithCosmicSource({
        sourceKey: this.currentFrequencySource,
        slotId: this.currentExoplanet,
        baseFrequency: this.sourceBaseFrequency,
      });
    }

    if (this._pendingSourceState) {
      const pending = this._pendingSourceState;
      this._pendingSourceState = null;
      this._duringScopedApply = true;
      try {
        this._applyStoredSourceState(pending.source, pending.frequency);
      } finally {
        this._duringScopedApply = false;
      }
    }

    if (!this._duringScopedApply) {
      this._persistCosmicState();
    }
    // React cosmic surface re-reads the source/waveform after world data seeds them.
    this._notifyCosmicChange();
  }

  setExoFrequencies(exoData) {
    this.setFrequencySources(exoData);
  }

  getExoBaseFrequency() {
    return this.sourceBaseFrequency;
  }

  applyTriggerMultiplier(multiplier) {
    const { dimensionId } = this.getScopedContext();
    const dimensionLFO = dimensionId ? this._ensureDimensionState(dimensionId) : null;

    // Use dimension-specific source base frequency if available
    const sourceBase = dimensionLFO?.sourceBaseFrequency ?? this.sourceBaseFrequency;
    const currentMult = dimensionLFO?.currentMultiplier ?? this.currentMultiplier;

    // Octave-fold + clamp the multiplied source base in the pure kernel.
    const { multiplier: newMultiplier, frequency: sanitized } = applyMultiplier(
      sourceBase,
      currentMult,
      multiplier,
    );
    if (sanitized == null) {
      console.warn(`CosmicLFO (${this.axis}): Unable to derive frequency for multiplier ${multiplier}.`);
      return;
    }

    this.currentMultiplier = newMultiplier;
    if (dimensionLFO) {
      dimensionLFO.currentMultiplier = newMultiplier;
    }

    this.setBaseFrequency(sanitized, {
      skipSourceAssignment: true,
      skipMultiplierReset: true,
      skipSourceBaseUpdate: true
    });
    // Note: setBaseFrequency already calls _persistCosmicState, so we don't need to call it again
  }

  _extractFrequencySources(rawSources = {}) {
    if (!rawSources || typeof rawSources !== 'object') {
      return {};
    }

    const normalized = {};

    Object.keys(SOURCE_DEFINITIONS).forEach((key) => {
      const rawCandidate = toFiniteNumber(rawSources[key]);
      if (!Number.isFinite(rawCandidate)) {
        return;
      }

      let harmonized = ensureHarmonicRange(rawCandidate);
      if (harmonized == null && rawCandidate === 0) {
        harmonized = MIN_FREQUENCY_HZ;
      }

      if (typeof harmonized === 'number') {
        normalized[key] = this._sanitizeFrequency(harmonized);
      }
    });

    if (!Object.keys(normalized).length) {
      const legacyValue =
        rawSources?.currentExoplanet?.minimum_cosmic_lfo ??
        rawSources?.currentExoplanet?.minimumCosmicLfo ??
        null;
      const parsedLegacy = ensureHarmonicRange(toFiniteNumber(legacyValue));
      if (typeof parsedLegacy === 'number') {
        normalized.minimumCosmicLfo = this._sanitizeFrequency(parsedLegacy);
      } else if (legacyValue === 0) {
        normalized.minimumCosmicLfo = MIN_FREQUENCY_HZ;
      }
    }

    // NOTHING is invented here. A world that carries no value for a source simply does not offer
    // that source: the map is what the world actually supplies, and an empty map means this world
    // has no astronomical data to modulate from at all. Standing a number in would put a real
    // source's label on a frequency no world ever derived.
    return normalized;
  }

  _reseedDimensionStatesWithCosmicSource({ sourceKey, slotId, baseFrequency }) {
    if (!sourceKey || sourceKey === MANUAL_SOURCE_KEY) {
      return;
    }
    const sanitized = this._sanitizeFrequency(baseFrequency);
    if (!Number.isFinite(sanitized) || sanitized <= 0) {
      return;
    }
    debugLog(this, '_reseedDimensionStatesWithCosmicSource', {
      sourceKey,
      slotId,
      baseFrequency,
      sanitized,
      cachedDimensions: Array.from(this.dimensionLFOs.keys()),
    });

    const normalizedState = {
      sourceKey,
      slotId: sourceKey,
      multiplier: 1,
      baseFrequency: sanitized,
    };

    const ids = this._getComponentIds();
    const { stackId } = this.getScopedContext();

    this.dimensionLFOs.forEach((dimensionLFO, dimensionId) => {
      if (!dimensionLFO) return;
      dimensionLFO.currentFrequencySource = sourceKey;
      dimensionLFO.currentExoplanet = normalizedState.slotId;
      dimensionLFO.sourceBaseFrequency = sanitized;
      dimensionLFO.currentMultiplier = 1;
      dimensionLFO.frequency = sanitized;

      if (stackId && dimensionId) {
        try {
          setScopedState(stackId, ids.source, normalizedState, { dimensionId });
          setScopedState(stackId, ids.frequency, sanitized, { dimensionId });
        } catch (error) {
          console.warn(`[CosmicLFO:${this.axis}] Failed to reseed cosmic source for ${dimensionId}`, error);
        }
      }
    });

    // Ensure current dimension is also updated even if it has not been created yet
    const { dimensionId: activeDimensionId } = this.getScopedContext();
    if (stackId && activeDimensionId) {
      try {
        setScopedState(stackId, ids.source, normalizedState, { dimensionId: activeDimensionId });
        setScopedState(stackId, ids.frequency, sanitized, { dimensionId: activeDimensionId });
      } catch (error) {
        console.warn(`[CosmicLFO:${this.axis}] Failed to persist active dimension cosmic source`, error);
      }
    }

    this.currentExoplanet = normalizedState.slotId;
    this.sourceBaseFrequency = sanitized;
    this.baseFrequency = sanitized;
    this.currentMultiplier = 1;
  }

  /**
   * A ×0.5 / ×2 frequency kick — momentary, and it moves the effective frequency, so in a room it
   * is published as the STEP rather than left to the frequency value alone: the accumulated
   * multiplier is what the next kick is computed from, and a value cannot carry it.
   * @param {string} triggerLabel - The legacy `${axis}Cosmic1` (×0.5) / `${axis}Cosmic2` (×2) key.
   * @param {{ roomRemote?: boolean }} [options] `roomRemote` marks a peer's kick being applied.
   */
  triggerKick(triggerLabel, { roomRemote = false } = {}) {
    let multiplier;
    if (triggerLabel.endsWith('1')) {
      multiplier = 0.5;
    } else if (triggerLabel.endsWith('2')) {
      multiplier = 2;
    } else {
      console.warn(`CosmicLFO (${this.axis}): Unknown trigger label '${triggerLabel}'.`);
      return;
    }
    if (this._roomRefuses(roomRemote)) {
      this._notifyCosmicChange(); // uniform with the other three: let the panel re-read the truth
      return;
    }
    this.applyTriggerMultiplier(multiplier);
    this._shareRoomAct(roomRemote, 'publishKick', multiplier === 0.5 ? 'half' : 'double');
  }

  initialize(exoData) {
  }

  computeFrequenciesFromExoData() {
    console.warn(`CosmicLFO (${this.axis}): computeFrequenciesFromExoData() not implemented.`);
  }


  dispose() {
    this._visualSamplerActive = false;
    this._cancelScheduledSample();
    this._unbindTempoFollow(); // the deck outlives this LFO — leave nothing subscribed to it
    this._activeDimensions.clear();
    this.isActive = false;
    this._lastVisualTimestamp = null;
    this._lastPushedParamValue = null;

    if (typeof document !== 'undefined' && this._boundVisibilityChange) {
      document.removeEventListener('visibilitychange', this._boundVisibilityChange);
    }
    this._boundVisibilityChange = null;

    if (typeof document !== 'undefined' && this._boundDimensionListener) {
      document.removeEventListener('orbiters:dimension-changed', this._boundDimensionListener);
    }
    this._boundDimensionListener = null;

    // Cosmic surface: tear down the freq + amplitude PM bridge subscriptions.
    if (this._freqParamController && typeof this.parameterManager?.unsubscribe === 'function') {
      this.parameterManager.unsubscribe(this._freqParamController, cosmicFrequencyParamId(this.axis));
    }
    this._freqParamController = null;
    this._freqParamBound = false;
    this._applyingFreqFromParam = false;
    if (this._ampParamController && typeof this.parameterManager?.unsubscribe === 'function') {
      this.parameterManager.unsubscribe(this._ampParamController, this._amplitudeParamId());
    }
    this._ampParamController = null;
    this._ampParamBound = false;
    this._applyingAmpFromParam = false;

  }
}
